import { useState } from "react";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { usePageMeta } from "@/hooks/usePageMeta";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getReadableErrorMessage = (rawMessage: string) => {
  try {
    const parsed = JSON.parse(rawMessage);
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0]?.message === "string") {
      return parsed[0].message;
    }
  } catch {
  }

  return rawMessage;
};

export default function Login() {
  usePageMeta({
    title: "ログイン | Biz-AI検定",
    description: "Biz-AI検定にログインして、受験申し込みや試験結果の確認ができます。",
  });

  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const search = useSearch();
  const redirectTo = new URLSearchParams(search).get("redirect") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: async (data) => {
      utils.auth.me.setData(undefined, data.user);
      await utils.auth.me.invalidate();
      toast.success("ログインしました");
      // 管理者の場合は管理者画面にリダイレクト
      if (data.user.role === "admin") {
        setLocation("/admin");
      } else {
        setLocation(redirectTo);
      }
    },
    onError: (error) => {
      toast.error(getReadableErrorMessage(error.message));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!EMAIL_REGEX.test(email)) {
      toast.error("有効なメールアドレスを入力してください");
      return;
    }

    if (!password.trim()) {
      toast.error("パスワードを入力してください");
      return;
    }

    loginMutation.mutate({ email, password });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <a href="/" className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Biz-AI
          </a>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">ログイン</CardTitle>
          <CardDescription className="text-center">
            Biz-AIにログイン
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">メールアドレス</Label>
              <Input
                id="email"
                type="email"
                placeholder="example@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">パスワード</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? "ログイン中..." : "ログイン"}
            </Button>
          </form>
          <div className="mt-3 text-center text-sm">
            <button
              onClick={() => setLocation("/request-reset")}
              className="text-blue-600 hover:underline"
            >
              パスワードを忘れた方はこちら
            </button>
          </div>
          <div className="mt-3 text-center text-sm">
            <span className="text-muted-foreground">アカウントをお持ちでない方は</span>{" "}
            <button
              onClick={() => setLocation(redirectTo !== "/" ? `/signup?redirect=${encodeURIComponent(redirectTo)}` : "/signup")}
              className="text-primary hover:underline font-medium"
            >
              新規登録
            </button>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
